import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, Col, Divider, Image, Row, Space, Typography } from 'antd'
import ProfileAvatar from '../../../helpers/ProfileAvatar/ProfileAvatar'
import moment from 'moment'
import { Link } from 'react-router-dom'
import { Brand, TCallList } from '../../../../context/types'
import { useAuthContext } from '../../../../context/auth'
import { amountToCurrencyFormat } from '../../../../utils/format'
import ReviewResponse from '../../../helpers/ReviewResponse/ReviewResponse'
import Review from '../../../helpers/Review/Review'
import { callApi, TApiResponse } from '../../../../context/api'
import { DownloadOutlined } from '@ant-design/icons'
import { BrandKey, brands } from '../../../../config'
import logo from '../../../../images/icons/logo.png'
import { CancelledCallAlert } from './CancelledCallAlert'

const { Text, Title } = Typography

interface TCallDetailsProps {
	record?: TCallList
}

function CallDetails({ record }: TCallDetailsProps) {
	const { auth } = useAuthContext()
	const [urls, setUrls] = useState<string[]>()
	const isMember = useMemo(() => record.user.isExpert, [record, auth])

	if (!record) {
		return null
	}

	const getMediaUrls = async () => {
		const mediaResponse: TApiResponse = await callApi(`/call/media/${record.id}`, {})
		setUrls(mediaResponse?.payload?.data?.urls)
	}

	useEffect(() => {
		if (isMember && record.is_media_available) {
			getMediaUrls()
		}
		return setUrls(undefined)
	}, [record.is_media_available])

	const UserLink = () =>
		record.user.isExpert ? (
			<Link to={`/expert/${record.user?.expert_slug}`}>
				{record.user?.first_name} {record.user?.last_name}
			</Link>
		) : (
			<span>
				{record.user?.first_name} {record.user?.last_name}
			</span>
		)

	const CallTitle = () =>
		record.user.isExpert ? (
			<span>
				me &#8594; <UserLink />
			</span>
		) : (
			<span>
				<UserLink /> &#8594; me
			</span>
		)

	const DownloadLink = () => {
		if (record.is_media_available === null || !isMember) {
			return null
		}

		if (record.is_media_available === false) {
			return (
				<Button type="primary" shape="round" loading>
					Preparing file...
				</Button>
			)
		}

		return urls ? (
			urls.length === 1 ? (
				<Button
					type="primary"
					shape="round"
					icon={<DownloadOutlined />}
					size="middle"
					style={{ height: 32, lineHeight: '32px' }}
					href={urls[0]}
					target="_blank"
					download
				>
					Download Recording
				</Button>
			) : (
				<Row gutter={[10, 10]}>
					{urls.map((url: string, index) => (
						<Col xs={24} key={url}>
							<Button
								type="primary"
								shape="round"
								icon={<DownloadOutlined />}
								size="middle"
								style={{ height: 32, lineHeight: '32px' }}
								href={url}
								target="_blank"
								download
							>
								Download Recording {index + 1}
							</Button>
						</Col>
					))}
				</Row>
			)
		) : null
	}

	const renderBrand = (brand: Partial<Brand>) => {
		const host = new URL(brand.root_url).hostname.replace('app.', '').replace('dev.', '').split('.')[0] as BrandKey

		return (
			<Image
				style={{ width: 70, marginBottom: -35, marginLeft: 25 }}
				src={brands[host].logoUrl || logo}
				preview={false}
				referrerPolicy="no-referrer"
			/>
		)
	}

	return (
		<>
			{renderBrand(record.brand)}

			{record.cancelled_on && record.cancelled_by && (
				<div className="cancelled-call-alert">
					<Alert
						showIcon
						type="warning"
						message={
							<CancelledCallAlert
								tz={auth?.user?.timezone}
								cancelledBy={record.cancelled_by}
								cancelledOn={record.cancelled_on}
							/>
						}
					/>
				</div>
			)}

			<div className="call-details">
				<Row className="call-details-row" wrap={false} gutter={[0, 20]}>
					<Col flex="none">
						<ProfileAvatar className="call-details-avatar" src={record?.user?.profile_pic_url} size={77} />
					</Col>
					<Col flex="auto">
						<Space direction="vertical" size="small" className="call-details-content" style={{ minHeight: '100%' }}>
							<Text strong>
								<CallTitle />
							</Text>
							<Text>{record?.reason}</Text>
							{record?.cost_to_user && record?.user?.expertPageId ? (
								<div>Pay ${amountToCurrencyFormat(record?.cost_to_user)}</div>
							) : null}
							{record?.spent_units_amount ? (
								<Text>
									Spent {record?.spent_units_amount} {record?.credits_based ? 'credits' : 'call'}
								</Text>
							) : null}
							{urls?.length > 1 && isMember && (
								<Alert
									type="info"
									showIcon
									style={{ marginBottom: 10, marginTop: 'auto' }}
									message="Note:If you have more than one link, you likely left the room and reconnected the call."
								/>
							)}
						</Space>
					</Col>
					<Col flex="none" className="call-details-date" xs={6}>
						<Space direction="vertical" size="small">
							<Card style={{ backgroundColor: '#EBF7FF' }}>
								<Title level={4} style={{ marginBottom: 0 }}>
									{moment(record?.start_time).tz(auth?.user?.timezone).date()}
								</Title>
								<Text>{moment(record?.start_time).tz(auth?.user?.timezone).format('MMMM')}</Text>
								<br />
								<Text>{moment(record?.start_time).tz(auth?.user?.timezone).format('YYYY')}</Text>
							</Card>
							<Text>{`${moment(record?.start_time).tz(auth?.user?.timezone).format('h:mm A')} ${moment
								.tz(auth?.user?.timezone)
								.format('z')}`}</Text>
							{record.call_link ? (
								<a rel="noreferrer" href={record.call_link} target="_blank">
									Join Call
								</a>
							) : (
								<DownloadLink />
							)}
						</Space>
					</Col>
				</Row>
				{!!record.expertReview && !!record.expertReview[0] && !record.user.isExpert && (
					<>
						<Divider />
						<ReviewResponse review={record.expertReview[0]} user={auth?.user} />
					</>
				)}
				{!!record.expertReview && !!record.expertReview[0] && !!record.user.isExpert && (
					<>
						<Divider />
						<Space direction="vertical" size="large" style={{ width: '100%' }}>
							<Title level={4} style={{ margin: 0 }}>
								About a call
							</Title>
							<Review review={record.expertReview[0]} user={auth?.user} />
						</Space>
					</>
				)}
			</div>
		</>
	)
}

export default CallDetails
